/**
 * Run acceptance-verification panel (NC-1235 V2 Scope 2) — mounted on
 * RunDetailsPage (Übersicht tab). Operator ask (2026-08-05): "verify als Button
 * anbieten und das Ergebnis als Log in der Anwendung zeigen."
 *
 * Two halves:
 *  1. "Lauf prüfen" — dispatches runs-verify (#1255) → run-verify.yml, a
 *     read-only wrapper around `ops.ps1 run verify`. Mirrors the BE's 5-min
 *     dispatch lock and its status gate so the button disables instead of
 *     collecting 409s/400s.
 *  2. The last report — verdict badge, timestamp, and the probe matrix grouped
 *     by section (collapsible), with the KnownIssue reference on accepted WARNs.
 *
 * `lastVerification` is written by the workflow's Cosmos persist (V1 #1234,
 * still a draft PR at the time of writing). Until that lands the panel renders
 * its "noch keine Prüfung" state and points at the job log — that is the normal
 * state today, not an error. Panel shape follows RunMaterialsPanel (NC-1098).
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { runsApi, extractErrorMessage } from '../services/api';
import { RunLastVerification, RunVerificationProbe } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { useExpiringLock } from '../hooks/useExpiringLock';

/** Mirrors the BE 5-min lock in runs-verify/index.ts. */
const VERIFY_LOCK_MS = 5 * 60 * 1000;

/**
 * Statuses runs-verify rejects with 400: `planned` was never provisioned and
 * `deleted` is torn down (that case is covered by verify-run's -ExpectCleaned,
 * which the trigger deliberately does not expose). Everything else — including
 * `error` — is verifiable: a broken run is exactly when the tutor needs the matrix.
 */
const UNVERIFIABLE_STATUSES = ['planned', 'deleted'];

type ChipColor = 'success' | 'warning' | 'error' | 'default' | 'info';

const VERDICT_STYLE: Record<RunLastVerification['verdict'], { color: ChipColor; label: string }> = {
  PASS: { color: 'success', label: 'Bestanden' },
  WARN: { color: 'warning', label: 'Mit Einschränkungen' },
  FAIL: { color: 'error', label: 'Fehlgeschlagen' },
};

const PROBE_STYLE: Record<RunVerificationProbe['status'], { color: ChipColor; label: string }> = {
  PASS: { color: 'success', label: 'PASS' },
  WARN: { color: 'warning', label: 'WARN' },
  FAIL: { color: 'error', label: 'FAIL' },
  SKIP: { color: 'default', label: 'SKIP' },
};

/**
 * Compact verdict chip — also used by the runs overview (RunsPage) so the
 * verdict→colour mapping lives in exactly one place.
 */
export function RunVerdictChip({
  verdict,
  size = 'small',
}: {
  verdict: RunLastVerification['verdict'];
  size?: 'small' | 'medium';
}) {
  const style = VERDICT_STYLE[verdict];
  if (!style) return null;
  return (
    <Chip
      size={size}
      color={style.color}
      label={style.label}
      variant="outlined"
      data-testid={`verdict-chip-${verdict}`}
    />
  );
}

interface Props {
  runId: string;
  runStatus?: string;
  lastVerification?: RunLastVerification;
  /** ISO — set when a verify was last dispatched; drives the 5-min lock display. */
  verifyRequestedUtc?: string;
  verifyJobUrl?: string;
  /** operator+ only — the parent gates rendering, this is a defensive echo. */
  canOperate: boolean;
  /** Lets the parent re-fetch the run once a dispatch has been accepted. */
  onDispatched?: () => void;
}

export function RunVerificationPanel({
  runId,
  runStatus,
  lastVerification,
  verifyRequestedUtc,
  verifyJobUrl,
  canOperate,
  onDispatched,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mirror the BE 5-min dispatch lock so the button disables instead of 409-ing.
  // NC-1258: via useExpiringLock, which ticks the lock down and releases it on
  // its own — the previous useMemo froze Date.now() and never re-enabled the
  // button without a remount.
  const lockSecondsAgo = useExpiringLock(verifyRequestedUtc, VERIFY_LOCK_MS);

  const lockActive = lockSecondsAgo !== null;
  const statusBlocked = !!runStatus && UNVERIFIABLE_STATUSES.includes(runStatus);
  const disabled = busy || lockActive || statusBlocked;

  const tooltip = statusBlocked
    ? `Ein Lauf im Status "${runStatus}" kann nicht geprüft werden — die Prüfung braucht einen bereitgestellten Lauf.`
    : lockActive
      ? `Eine Prüfung läuft bereits (vor ${lockSecondsAgo}s gestartet) — bitte warten.`
      : 'Startet die Abnahme-Prüfung (read-only) und schreibt das Ergebnis in diesen Lauf.';

  // Group probes by section; the compact projection carries no `question`/raw
  // output, so the matrix shows Probe / Persona / Status / Detail.
  const sections = useMemo(() => {
    const probes = lastVerification?.probes ?? [];
    const bySection = new Map<string, RunVerificationProbe[]>();
    probes.forEach((p) => {
      const list = bySection.get(p.section);
      if (list) list.push(p);
      else bySection.set(p.section, [p]);
    });
    return Array.from(bySection.entries());
  }, [lastVerification]);

  const triggerVerify = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await runsApi.verify(runId);
      setMessage(
        res.jobUrl
          ? 'Prüfung gestartet — das Ergebnis erscheint hier, sobald der Lauf durch ist.'
          : 'Prüfung gestartet.'
      );
      onDispatched?.();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canOperate) return null;

  return (
    <Card data-testid="run-verification-panel">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">Abnahme-Prüfung</Typography>
          {lastVerification && <RunVerdictChip verdict={lastVerification.verdict} />}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <Tooltip title={tooltip}>
            <span>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress size={18} /> : <FactCheckIcon />}
                disabled={disabled}
                onClick={triggerVerify}
                data-testid="verify-btn"
              >
                Lauf prüfen
              </Button>
            </span>
          </Tooltip>
          {verifyRequestedUtc && (
            <Chip
              size="small"
              variant="outlined"
              color={lockActive ? 'warning' : 'default'}
              label={`Zuletzt angefordert: ${formatDateSafe(verifyRequestedUtc, 'dd.MM.yyyy HH:mm')}`}
            />
          )}
          {verifyJobUrl && (
            <Link href={verifyJobUrl} target="_blank" rel="noopener" variant="body2">
              Job-Log
            </Link>
          )}
        </Stack>

        <Box sx={{ mt: 2 }}>
          {!lastVerification ? (
            <Typography variant="body2" color="text.secondary" data-testid="verify-empty">
              Noch keine Prüfung für diesen Lauf. Nach dem Start steht das Ergebnis im Job-Log und
              erscheint anschließend hier.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" data-testid="verify-summary">
                {formatDateSafe(lastVerification.generatedUtc, 'dd.MM.yyyy HH:mm')} ·{' '}
                {lastVerification.counts.pass} PASS · {lastVerification.counts.warn} WARN ·{' '}
                {lastVerification.counts.fail} FAIL · {lastVerification.counts.skip} SKIP
                {lastVerification.sectionsRun.length > 0 &&
                  ` · Sektionen: ${lastVerification.sectionsRun.join(', ')}`}
              </Typography>

              {sections.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Der Bericht enthält keine Einzelproben.
                </Typography>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  {sections.map(([section, probes]) => {
                    const worst = probes.some((p) => p.status === 'FAIL')
                      ? 'FAIL'
                      : probes.some((p) => p.status === 'WARN')
                        ? 'WARN'
                        : 'PASS';
                    return (
                      <Accordion key={section} disableGutters data-testid={`verify-section-${section}`}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2">{section}</Typography>
                            <Chip
                              size="small"
                              color={PROBE_STYLE[worst].color}
                              label={`${probes.length} Proben`}
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Probe</TableCell>
                                <TableCell>Persona</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Detail</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {probes.map((p) => (
                                <TableRow key={p.id} data-testid={`verify-probe-${p.id}`}>
                                  <TableCell>{p.id}</TableCell>
                                  <TableCell>{p.persona}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      color={PROBE_STYLE[p.status].color}
                                      label={PROBE_STYLE[p.status].label}
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {p.detail || '—'}
                                    {p.knownIssue && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Bekanntes Problem: {p.knownIssue}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default RunVerificationPanel;
